import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const input = "w-full bg-surface border border-gold/20 rounded-xl px-4 py-2.5 text-cream placeholder:text-faded focus:outline-none focus:ring-1 focus:ring-gold text-sm";
const empty = (cat) => ({ name: "", category: cat, price: 0, description: "", image: "", duration: "", active: true });

function Crud({ endpoint, defaultCat }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty(defaultCat));
    const [editId, setEditId] = useState(null);

    const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form, price: parseFloat(form.price) };
        try {
            if (editId) await api.put(`/admin/${endpoint}/${editId}`, payload);
            else await api.post(`/admin/${endpoint}`, payload);
            toast.success("Saved"); setOpen(false); setForm(empty(defaultCat)); setEditId(null); load();
        } catch { toast.error("Could not save"); }
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/${endpoint}/${id}`); toast.success("Deleted"); load(); };

    return (
        <div>
            <div className="flex justify-end mb-4">
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty(defaultCat)); setEditId(null); } }}>
                    <DialogTrigger asChild>
                        <button data-testid={`add-${endpoint}-btn`} className="inline-flex items-center gap-2 bg-gold text-ink rounded-full px-5 py-2.5 text-sm font-medium hover:bg-goldbright"><Plus className="w-4 h-4" /> New</button>
                    </DialogTrigger>
                    <DialogContent className="bg-surface2 border-gold/20 text-cream max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "New"}</DialogTitle></DialogHeader>
                        <form onSubmit={save} className="space-y-3">
                            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
                            <div className="grid grid-cols-2 gap-3">
                                <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input} />
                                <input placeholder="Duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={input} />
                            </div>
                            <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={input} />
                            <input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={input} />
                            <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} />
                            <label className="flex items-center gap-2 text-sm text-ash"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-gold" /> Active</label>
                            <button className="w-full bg-gold text-ink rounded-full py-2.5 font-medium">Save</button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                {items.map((s) => (
                    <div key={s.id} className="p-5 rounded-2xl bg-surface border border-gold/10 flex gap-4">
                        <img src={s.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                        <div className="flex-1">
                            <div className="text-cream">{s.name}</div>
                            <div className="text-xs text-faded">{s.category} · {s.duration}</div>
                            <div className="text-gold text-sm mt-1">${s.price.toFixed(2)}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button onClick={() => { setForm(s); setEditId(s.id); setOpen(true); }} className="text-ash hover:text-gold"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => del(s.id)} className="text-ash hover:text-clay"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AdminServices() {
    return (
        <div>
            <h1 className="font-serif text-4xl text-cream mb-6">Services &amp; Readings</h1>
            <Tabs defaultValue="services">
                <TabsList className="bg-surface2 border border-gold/15">
                    <TabsTrigger value="services" className="data-[state=active]:bg-gold data-[state=active]:text-ink">Services</TabsTrigger>
                    <TabsTrigger value="readings" className="data-[state=active]:bg-gold data-[state=active]:text-ink">Readings</TabsTrigger>
                </TabsList>
                <TabsContent value="services" className="mt-6"><Crud endpoint="services" defaultCat="Spellwork" /></TabsContent>
                <TabsContent value="readings" className="mt-6"><Crud endpoint="readings" defaultCat="Tarot" /></TabsContent>
            </Tabs>
        </div>
    );
}
